import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { QueryParameters, ResponseObj } from '../interface';
import AuthService from './auth.service';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

export const cancelToken = axios.CancelToken;

export interface AxiosParams extends MiscellaneousRequestParams {
	method: string;
	url: string;
	data?: any;
}

export interface MiscellaneousRequestParams {
	isAccessTokenRequire?: boolean;
	contentType?: string;
	responseType?: string;
	xApi?: number | string | null;
	noCache?: boolean;
	dedupeInFlight?: boolean;
	streamUserId?: string | null;
	apiKey?: string | null;
	executor?: (cancel: Canceler) => void;
	cacheTtlMs?: number;
}

type InvalidateExactOptions = {
	url: string;
	method?: string;
	authToken?: string;
};

class HttpService {
	CancelToken = axios.CancelToken;
	cancel_req: any = {};
	baseUrl: string;
	name: string;
	axiosInstance: AxiosInstance;
	private responseCache = new Map<string, { expiresAt: number; value: any }>();
	private pendingRequests = new Map<string, Promise<any>>();
	private featureScopePrefixes: Record<string, string[]> = {
		admin_news: ['admin/news', 'admin/permissions/newsList'],
		admin_materials: ['admin/material', 'admin/permissions/courselist'],
		admin_forecast: ['admin/forecast', 'admin/permissions/foreCast'],
		admin_pdf: ['admin/pdfModule', 'admin/permissions/pdfModule'],
		whitelabel_news: ['admin/whitelabel/news', 'admin/whitelabel/all_news'],
		whitelabel_materials: ['admin/whitelabel/add-material-list', 'admin/whitelabel/drag-drop-materials-list', 'admin/material/all_materials'],
		dashboard: ['dashboard/'],
		news: ['news/'],
		materials: ['materials/']
	};

	constructor(axiosInstance: AxiosInstance, baseUrl: string, name: string) {
		this.axiosInstance = axiosInstance;
		this.baseUrl = baseUrl;
		this.name = name;
	}

	getUrl = (url: string, params: QueryParameters | any = {}): string => {
		if (!url.includes('https')) {
			let urlString = `${this.baseUrl}/${url}`;
			if (params && !isEmpty(params)) {
				Object.keys(params).forEach((key) => {
					if (params[key] == null || params[key] === '') {
						delete params[key];
					}
				});
				urlString += `?${queryString.stringify(params)}`;
			}
			return urlString;
		}
		return url;
	};

	/**
	 * get method
	 * @param request object containing axios params
	 */
	get = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
		this.commonAxios({ method: 'GET', url: this.getUrl(url, params), ...otherData });

	/**
	 * post method
	 * @param request object containing axios params
	 */
	post = (url: string, params: any = {}, queryParams = {}, otherData: MiscellaneousRequestParams = {}) =>
		this.commonAxios({
			method: 'POST',
			url: this.getUrl(url, queryParams),
			data: params,
			...otherData
		});

	/**
	 * put method
	 * @param request object containing axios params
	 */
	put = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
		this.commonAxios({
			method: 'PUT',
			url: this.getUrl(url),
			data: params,
			...otherData
		});

	/**
	 * deleteRequest method
	 * @param request object containing axios params
	 */
	deleteRequest = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
		this.commonAxios({
			method: 'DELETE',
			url: this.getUrl(url),
			data: params,
			...otherData
		});

	/**
	 * patch method
	 * @param request object containing axios params
	 */
	patch = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) =>
		this.commonAxios({
			method: 'PATCH',
			url: this.getUrl(url),
			data: params,
			...otherData
		});

	/**
	 * commonAxios
	 * @param object containing method, url, data, access token, content-type
	 */
	commonAxios = ({
		method,
		url,
		data,
		isAccessTokenRequire = true,
		contentType = 'application/json',
		responseType,
		xApi = '',
		noCache = false,
		dedupeInFlight = true,
		streamUserId = '',
		apiKey = '',
		executor = (c) => (this.cancel_req = c),
		cacheTtlMs = 30000
	}: AxiosParams): Promise<any> => {
		const headers: any = {
			'Content-Type': contentType
		};
		const token = isAccessTokenRequire && AuthService.getAccessToken();
		if (apiKey) {
			headers['api-key'] = `${apiKey}`;
		}
		if (streamUserId) {
			headers['stream-user-id'] = streamUserId;
		}
		if (xApi) {
			headers['x-api-key'] = `${xApi}`;
		}
		if (noCache) {
			headers['Cache-Control'] = 'no-cache, no-store, must-revalidate, max-age=0';
		}
		if (token) {
			headers['authorization'] = `${token}`;
		} else {
			// headers['x-request-language'] = localStorage.getItem('lang');
		}
		let body: any = null;
		if (contentType === 'application/json') {
			body = JSON.stringify(data);
		} else {
			body = data;
		}
		const shouldCache = method === 'GET' && !noCache && cacheTtlMs > 0 && responseType !== 'arraybuffer' && responseType !== 'blob';
		const shouldDedupeInFlight = dedupeInFlight && !noCache;
		const cacheKey = shouldCache ? `${method}:${url}:${headers['authorization'] || ''}` : '';
		if (shouldCache) {
			const cached = this.responseCache.get(cacheKey);
			if (cached && cached.expiresAt > Date.now()) {
				return Promise.resolve(cached.value);
			}
			if (cached) {
				this.responseCache.delete(cacheKey);
			}
		}
		const pendingKey = this.buildPendingRequestKey(method, url, headers, body);
		if (shouldDedupeInFlight) {
			const existingRequest = this.pendingRequests.get(pendingKey);
			if (existingRequest) {
				return existingRequest;
			}
		}
		const requestPromise = new Promise((resolve, reject) => {
			this.axiosInstance({
				method: method,
				url: url,
				cancelToken: new axios.CancelToken(executor),
				headers: headers,
				responseType: responseType,
				data: body
			} as AxiosRequestConfig)
				.then((response: AxiosResponse<ResponseObj<any>>) => {
					if (response) {
						if (contentType === 'application/json') {
							const resolved =
							response.data instanceof ArrayBuffer || response.data instanceof Blob ? response : response.data.data;
							if (shouldCache) {
								this.responseCache.set(cacheKey, { expiresAt: Date.now() + cacheTtlMs, value: resolved });
							}
							resolve(resolved);
						} else {
							if (shouldCache) {
								this.responseCache.set(cacheKey, { expiresAt: Date.now() + cacheTtlMs, value: response.data });
							}
							resolve(response.data);
						}
					} else {
						resolve({ reqCanceled: true });
					}
				})
				.catch((error: Error) => {
					reject(error);
				});
		});
		if (shouldDedupeInFlight) {
			this.pendingRequests.set(pendingKey, requestPromise);
			return requestPromise.finally(() => {
				this.pendingRequests.delete(pendingKey);
			});
		}
		return requestPromise;
	};

	private hashString = (value: string): string => {
		let hash = 0;
		for (let i = 0; i < value.length; i++) {
			hash = (hash << 5) - hash + value.charCodeAt(i);
			hash |= 0;
		}
		return String(hash);
	};

	private buildPendingRequestKey = (method: string, url: string, headers: Record<string, string>, body: unknown) => {
		const token = headers['x-access-token'] || headers['authorization'] || '';
		let serializedBody = '';
		if (typeof body === 'string') {
			serializedBody = body;
		} else if (body == null) {
			serializedBody = '';
		} else {
			try {
				serializedBody = JSON.stringify(body);
			} catch {
				serializedBody = String(body);
			}
		}
		return `${method}:${url}:${token}:${this.hashString(serializedBody)}`;
	};

	private clearByPredicate = (predicate: (key: string) => boolean) => {
		Array.from(this.responseCache.keys()).forEach((key) => {
			if (predicate(key)) {
				this.responseCache.delete(key);
			}
		});
		Array.from(this.pendingRequests.keys()).forEach((key) => {
			if (predicate(key)) {
				this.pendingRequests.delete(key);
			}
		});
	};

	invalidateCacheExact = ({ url, method = 'GET', authToken }: InvalidateExactOptions) => {
		const normalizedMethod = method.toUpperCase();
		if (authToken) {
			const exactKey = `${normalizedMethod}:${url}:${authToken}`;
			this.clearByPredicate((key) => key === exactKey || key.startsWith(`${exactKey}:`));
			return;
		}
		const keyPrefix = `${normalizedMethod}:${url}:`;
		this.clearByPredicate((key) => key.startsWith(keyPrefix));
	};

	invalidateCacheByPrefix = (prefixes: string | string[]) => {
		const normalizedPrefixes = (Array.isArray(prefixes) ? prefixes : [prefixes]).filter(Boolean);
		if (!normalizedPrefixes.length) {
			return;
		}
		this.clearByPredicate((key) => normalizedPrefixes.some((prefix) => key.includes(prefix)));
	};

	invalidateFeatureScope = (scope: string) => {
		const prefixes = this.featureScopePrefixes[scope];
		if (!prefixes?.length) {
			return;
		}
		this.invalidateCacheByPrefix(prefixes);
	};

	clearCache = () => {
		this.responseCache.clear();
		this.pendingRequests.clear();
	};

	getBlob = (url: string) =>
		new Promise((resolve, reject) => {
			axios
				.get(url, { responseType: 'blob' })
				.then((response: any) => {
					resolve(response.data);
				})
				.catch((error: Error) => reject(error));
		});
}

export default HttpService;
